import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

interface AuthSessionSource {
  getSession: () => Promise<{ data: { session: Session | null }; error: unknown }>;
  onAuthStateChange: (
    callback: (event: AuthChangeEvent, session: Session | null) => void,
  ) => { data: { subscription: { unsubscribe: () => void } } };
}

/** Restore a session without allowing a late restore to undo a newer auth event. */
export function observeAuthSession(
  auth: AuthSessionSource,
  handlers: {
    onSession: (session: Session | null) => void;
    onIdentityChange: (previous: string | null | undefined, next: string | null) => void;
  },
): () => void {
  let disposed = false;
  let revision = 0;
  let identity: string | null | undefined;

  const accept = (session: Session | null) => {
    if (disposed) return;
    const validSession = session?.user?.id ? session : null;
    const nextIdentity = validSession?.user.id ?? null;
    if (identity !== nextIdentity) {
      // Clear data before publishing the new identity to UI subscribers.
      handlers.onIdentityChange(identity, nextIdentity);
      identity = nextIdentity;
    }
    handlers.onSession(validSession);
  };

  // Keep this callback synchronous: Supabase invokes it under its auth lock.
  const restoreRevision = revision;
  const { data: { subscription } } = auth.onAuthStateChange((_event, session) => {
    revision += 1;
    accept(session);
  });
  void auth.getSession().then(({ data, error }) => {
    if (revision === restoreRevision) accept(error ? null : data.session);
  }).catch(() => {
    if (revision === restoreRevision) accept(null);
  });

  return () => {
    disposed = true;
    subscription.unsubscribe();
  };
}
